import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  SimpleGrid,
  Select,
  Input,
  Textarea,
  CurrencyInput,
} from '@wegroup/design-system';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { CompanyLoan, Loan } from '../../../../types/CompanyScanTypes';

interface Props {
  companyLoan?: CompanyLoan;
  isOpen: boolean;
  onClose: () => void;
  addCompanyLoan: (companyLoan: CompanyLoan) => void;
  editCompanyLoan: (companyLoan: CompanyLoan) => void;
}

const companyLoanValidationSchema = yup.object().shape({
  type: yup.string().required('Required'),
  duration: yup.number().typeError('Required'),
  amount: yup.number().typeError('Required'),
  purpose: yup.string(),
  lender_details: yup.string(),
});

const NewCompanyLoanModal: React.FC<React.PropsWithChildren<Props>> = ({
  companyLoan,
  addCompanyLoan,
  editCompanyLoan,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const modalGoal = companyLoan ? 'EDIT' : 'ADD';

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<CompanyLoan>({
    resolver: yupResolver(companyLoanValidationSchema),
    defaultValues: {
      type: companyLoan?.type,
      duration: companyLoan?.duration,
      amount: companyLoan?.amount,
      purpose: companyLoan?.purpose,
      lender_details: companyLoan?.lender_details,
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = handleSubmit((newCompanyLoan: CompanyLoan) => {
    if (companyLoan) editCompanyLoan(newCompanyLoan);
    else addCompanyLoan(newCompanyLoan);
    handleOnClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`FLOWS_COMPANY-SCAN_COMPANY-LOAN-INPUT_${modalGoal}-LOAN`)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={SimpleGrid} columns={2} gap="4">
          <FormControl isInvalid={!!errors.type} gridColumn="span 2">
            <FormLabel>{t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_TYPE')}</FormLabel>
            <Select {...register('type')}>
              {Object.keys(Loan).map((key) => (
                <option key={key} value={key}>
                  {t(`FLOWS_COMPANY-SCAN_LOAN-ENUM_${key}`)}
                </option>
              ))}
            </Select>
            {errors.type?.message && (
              <FormErrorMessage>{t(errors.type.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.duration}>
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_DURATION')}
            </FormLabel>
            <Input
              {...register('duration')}
              placeholder={t('MONTHS')}
              type="number"
            />
            {errors.duration?.message && (
              <FormErrorMessage>{t(errors.duration.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.amount}>
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_AMOUNT')}
            </FormLabel>
            <Controller
              name="amount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CurrencyInput
                  onChange={({ value }) => onChange(value)}
                  value={value}
                />
              )}
            />
            {errors.amount?.message && (
              <FormErrorMessage>{t(errors.amount.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.purpose} gridColumn="span 2">
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_PURPOSE')}
            </FormLabel>

            <Textarea {...register('purpose')} />
            {errors.purpose?.message && (
              <FormErrorMessage>{t(errors.purpose.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.lender_details} gridColumn="span 2">
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_LENDER-DETAILS')}
            </FormLabel>
            <Textarea {...register('lender_details')} />
            {errors.lender_details?.message && (
              <FormErrorMessage>
                {t(errors.lender_details.message)}
              </FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter as={HStack} justify="flex-end" spacing="4">
          <Button variant="ghost" mr={3} onClick={handleOnClose}>
            {t('CLOSE')}
          </Button>
          <Button onClick={handleOnSubmit}>
            {modalGoal ? t('SAVE') : t('ADD')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewCompanyLoanModal;
